import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Search, Globe, Loader2, Newspaper, Trophy, Sparkles, Star, X,
    Download, Languages, BookOpen, ExternalLink, Wand2,
} from "lucide-react";
import { toast } from "sonner";

const fmtKickoff = (iso) => {
    if (!iso) return "TBD";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const DiscoverWidget = ({ defaultKind = "all", onPicked }) => {
    const { token } = useAuth();
    const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [q, setQ] = useState("");
    const [debounced, setDebounced] = useState("");
    const [kind, setKind] = useState(defaultKind);
    const [selectedLeagues, setSelectedLeagues] = useState([]);
    const [leagues, setLeagues] = useState([]);
    const [results, setResults] = useState({ fixtures: [], articles: [] });
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const [tpItem, setTpItem] = useState(null);   // translate/publish modal
    const [tpFeature, setTpFeature] = useState(true);
    const [tpResult, setTpResult] = useState(null);
    const [tpBusy, setTpBusy] = useState(false);

    const [fxItem, setFxItem] = useState(null);   // import-fixture modal
    const [fxPredict, setFxPredict] = useState(true);
    const [fxFeature, setFxFeature] = useState(true);
    const [fxResult, setFxResult] = useState(null);
    const [fxBusy, setFxBusy] = useState(false);

    useEffect(() => {
        axios.get(`${API}/admin/content-hub/discover-leagues`, { headers: authH })
            .then((r) => setLeagues(r.data?.leagues || []))
            .catch(() => {});
    }, [authH]);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        // Allow league-only search (no q) — fetch when leagues picked OR q present
        if (!debounced && selectedLeagues.length === 0) {
            setResults({ fixtures: [], articles: [] });
            return;
        }
        const run = async () => {
            setLoading(true);
            try {
                const params = { limit: 18, kind };
                if (debounced) params.q = debounced;
                if (selectedLeagues.length) params.leagues = selectedLeagues.join(",");
                const r = await axios.get(`${API}/admin/content-hub/discover`, { headers: authH, params });
                setResults({ fixtures: r.data?.fixtures || [], articles: r.data?.articles || [] });
            } catch (e) {
                toast.error("Discovery failed", { description: e?.response?.data?.detail || e.message });
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [debounced, kind, selectedLeagues, authH]);

    const toggleLeague = (id) => {
        setSelectedLeagues((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    };

    const submitTranslatePublish = async () => {
        if (!tpItem) return;
        setTpBusy(true);
        try {
            const r = await axios.post(`${API}/admin/content-hub/translate-publish`, {
                kind: "article",
                subkind: tpItem.subkind || "rss",
                url: tpItem.url,
                title: tpItem.title,
                summary: tpItem.summary,
                image_url: tpItem.image_url,
                source: tpItem.source,
                feature_on_homepage: tpFeature,
            }, { headers: authH, timeout: 120000 });
            setTpResult(r.data);
            toast.success("Translated & published", { description: tpFeature ? "Featured on homepage" : "Saved as published article" });
            onPicked?.({ kind: "article", id: r.data?.content_id });
        } catch (e) {
            toast.error("Publish failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setTpBusy(false);
        }
    };

    const submitImportFixture = async () => {
        if (!fxItem) return;
        setFxBusy(true);
        try {
            const r = await axios.post(`${API}/admin/content-hub/import-fixture`, {
                event_id: fxItem.event_id,
                league_id: fxItem.league_id,
                league: fxItem.league,
                home_team: fxItem.home_team,
                away_team: fxItem.away_team,
                home_logo: fxItem.home_logo,
                away_logo: fxItem.away_logo,
                kickoff: fxItem.kickoff,
                venue: fxItem.venue,
                competition_id: fxItem.league_id,
                generate_prediction: fxPredict,
                feature_on_homepage: fxFeature,
            }, { headers: authH, timeout: 120000 });
            setFxResult(r.data);
            toast.success("Fixture imported", {
                description: [
                    r.data?.prediction_id ? "AI prediction generated" : null,
                    r.data?.featured ? "Featured on homepage" : null,
                ].filter(Boolean).join(" · ") || "Saved as scheduled match",
            });
            onPicked?.({ kind: "fixture", id: r.data?.match_id });
        } catch (e) {
            toast.error("Import failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setFxBusy(false);
        }
    };

    const fixtures = results.fixtures || [];
    const articles = results.articles || [];
    const total = fixtures.length + articles.length;
    const hasResults = total > 0;

    return (
        <div className="space-y-4" data-testid="discover-widget">
            <Card className="bg-gradient-to-br from-paper to-paper/60 border-emerald-400/30 p-5">
                <div className="flex items-center gap-2 mb-3 text-emerald-300">
                    <Globe className="h-4 w-4" />
                    <span className="text-[11px] uppercase tracking-[0.2em] font-heading font-bold">
                        Discover from the Web
                    </span>
                    <span className="text-[10px] text-white/40">ESPN · RSS · DuckDuckGo</span>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Team or competition (Arsenal, Simba, EPL, World Cup, AFCON, Bundesliga)…"
                        className="pl-11 pr-10 h-12 bg-obsidian border-border focus:border-emerald-400 text-white placeholder:text-white/30"
                        data-testid="discover-input"
                    />
                    {q && (
                        <button
                            onClick={() => setQ("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                            data-testid="discover-clear"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                {/* Kind toggle */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {[
                        { id: "all", label: "All", Icon: Sparkles },
                        { id: "fixture", label: "Fixtures", Icon: Trophy },
                        { id: "article", label: "Articles", Icon: Newspaper },
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setKind(f.id)}
                            data-testid={`discover-kind-${f.id}`}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                                kind === f.id
                                    ? "bg-emerald-400 text-obsidian border-emerald-400"
                                    : "bg-transparent text-white/60 border-border hover:border-emerald-400/60 hover:text-white"
                            }`}
                        >
                            <f.Icon className="h-3 w-3" />
                            {f.label}
                        </button>
                    ))}
                    <span className="ml-auto text-[11px] text-white/40">
                        {loading ? "Searching…" : total > 0 ? `${fixtures.length} fixtures · ${articles.length} articles` : "Type a team/competition or pick a league"}
                    </span>
                </div>

                {/* Competition chips */}
                <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Quick Competitions</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {leagues.map((lg) => {
                            const on = selectedLeagues.includes(lg.id);
                            return (
                                <button
                                    key={lg.id}
                                    onClick={() => toggleLeague(lg.id)}
                                    data-testid={`discover-league-${lg.id}`}
                                    className={`px-2.5 py-1 rounded text-[11px] font-medium border transition-colors ${
                                        on
                                            ? "bg-electric/20 text-electric border-electric"
                                            : "bg-obsidian text-white/60 border-border hover:border-electric/40 hover:text-white"
                                    }`}
                                >
                                    {lg.flag} {lg.label}
                                </button>
                            );
                        })}
                        {selectedLeagues.length > 0 && (
                            <button
                                onClick={() => setSelectedLeagues([])}
                                className="px-2 py-1 text-[11px] text-white/40 hover:text-white"
                                data-testid="discover-leagues-clear"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </Card>

            {loading && (
                <div className="flex items-center justify-center py-10 text-white/40">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Pulling fresh data from the web…
                </div>
            )}

            {/* Fixtures */}
            {!loading && fixtures.length > 0 && (
                <div className="space-y-2" data-testid="discover-fixtures">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50">
                        <Trophy className="h-3.5 w-3.5 text-yellow-300" />
                        Fixtures · {fixtures.length}
                    </div>
                    {fixtures.map((f) => (
                        <Card
                            key={`fx-${f.id}`}
                            className="bg-paper border-border p-4 hover:border-emerald-400/40 transition-colors"
                            data-testid={`discover-fixture-${f.id}`}
                        >
                            <div className="flex items-center gap-4 flex-wrap">
                                <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                                    {f.home_logo && (
                                        <img src={f.home_logo} alt="" className="w-8 h-8 object-contain bg-obsidian rounded" onError={(e) => {e.currentTarget.style.display="none";}} />
                                    )}
                                    <div className="text-white font-bold text-sm">{f.home_team}</div>
                                    <div className="text-white/40 text-xs">vs</div>
                                    {f.away_logo && (
                                        <img src={f.away_logo} alt="" className="w-8 h-8 object-contain bg-obsidian rounded" onError={(e) => {e.currentTarget.style.display="none";}} />
                                    )}
                                    <div className="text-white font-bold text-sm">{f.away_team}</div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className="text-[10px] border-electric/40 text-electric">
                                        {f.league_flag} {f.league}
                                    </Badge>
                                    <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                        {fmtKickoff(f.kickoff)}
                                    </Badge>
                                    {f.score && (
                                        <Badge className="bg-yellow-300/20 text-yellow-300 text-[10px] border-yellow-300/40 border font-mono">
                                            {f.score}
                                        </Badge>
                                    )}
                                    {f.status_label && (
                                        <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                            {f.status_label}
                                        </Badge>
                                    )}
                                </div>
                                <Button
                                    size="sm"
                                    disabled={busyId === f.id}
                                    onClick={() => { setFxItem(f); setFxResult(null); setFxPredict(true); setFxFeature(true); }}
                                    className="bg-emerald-400 text-obsidian hover:bg-emerald-300 font-bold"
                                    data-testid={`discover-fixture-import-${f.id}`}
                                >
                                    <Download className="h-3.5 w-3.5 mr-1.5" /> Import & Predict
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Articles */}
            {!loading && articles.length > 0 && (
                <div className="space-y-2" data-testid="discover-articles">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50">
                        <Newspaper className="h-3.5 w-3.5 text-blue-400" />
                        Articles · {articles.length}
                    </div>
                    {articles.map((a) => (
                        <Card
                            key={`ar-${a.id}`}
                            className="bg-paper border-border p-4 hover:border-emerald-400/40 transition-colors"
                            data-testid={`discover-article-${a.id}`}
                        >
                            <div className="flex items-start gap-4">
                                {a.image_url ? (
                                    <img src={a.image_url} alt="" className="w-20 h-20 rounded-md object-cover border border-border flex-shrink-0" onError={(e)=>{e.currentTarget.style.display="none";}} />
                                ) : (
                                    <div className="w-20 h-20 rounded-md bg-blue-400/10 flex items-center justify-center flex-shrink-0">
                                        <BookOpen className="h-7 w-7 text-blue-400/80" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                        <Badge variant="outline" className="text-[10px] border-blue-400/40 text-blue-400">
                                            {a.subkind === "web" ? "Web" : "RSS"}
                                        </Badge>
                                        {a.source && (
                                            <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                                {a.source}
                                            </Badge>
                                        )}
                                        {a.imported_content_id && (
                                            <Badge className="bg-emerald-400/20 text-emerald-300 border-emerald-400/40 border text-[10px]">
                                                ✓ Imported
                                            </Badge>
                                        )}
                                    </div>
                                    <h3 className="font-heading font-bold text-white text-base leading-snug">{a.title}</h3>
                                    {a.summary && <p className="text-white/55 text-sm mt-1 line-clamp-2">{a.summary}</p>}
                                </div>
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                    <Button
                                        size="sm"
                                        disabled={busyId === a.id}
                                        onClick={() => { setTpItem(a); setTpResult(null); setTpFeature(true); }}
                                        className="bg-emerald-400 text-obsidian hover:bg-emerald-300 font-bold"
                                        data-testid={`discover-article-publish-${a.id}`}
                                    >
                                        <Languages className="h-3.5 w-3.5 mr-1.5" /> Translate & Publish
                                    </Button>
                                    {a.url && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            asChild
                                            className="border-border text-white/70"
                                        >
                                            <a href={a.url} target="_blank" rel="noreferrer">
                                                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open
                                            </a>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {!loading && hasResults === false && (debounced || selectedLeagues.length > 0) && (
                <Card className="bg-paper border-border p-10 text-center text-white/40">
                    No matches. Try a different team, competition, or pick another league chip.
                </Card>
            )}

            {/* Translate & Publish dialog */}
            <Dialog open={!!tpItem} onOpenChange={(o) => !o && setTpItem(null)}>
                <DialogContent className="bg-paper border-border max-w-lg" data-testid="discover-tp-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Wand2 className="h-5 w-5 text-emerald-400" /> Translate & Publish
                        </DialogTitle>
                        <DialogDescription className="text-xs text-white/50">
                            Scrape full article body · GPT-5.2 Swahili translation · publish to public site.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm text-white/80 line-clamp-3">{tpItem?.title}</div>
                        <div className="text-[11px] text-white/40">{tpItem?.source} · {tpItem?.url || "(no URL)"}</div>
                        <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer pt-2">
                            <Checkbox
                                checked={tpFeature}
                                onCheckedChange={setTpFeature}
                                data-testid="discover-tp-feature"
                            />
                            <Star className="h-3.5 w-3.5 text-yellow-300" />
                            Feature on homepage immediately
                        </label>
                        {tpResult?.article && (
                            <Card className="bg-obsidian border-emerald-400/40 p-3">
                                <Badge className="bg-emerald-400/20 text-emerald-300 mb-2 text-[10px] uppercase">
                                    {tpResult.via === "rss_pipeline" ? "Via RSS pipeline" : "Via web scrape"}
                                </Badge>
                                <div className="text-sm font-bold text-white">{tpResult.article.title_sw}</div>
                                <p className="text-xs text-white/60 mt-1 line-clamp-4">{tpResult.article.summary_sw}</p>
                            </Card>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            disabled={tpBusy}
                            onClick={submitTranslatePublish}
                            className="bg-emerald-400 text-obsidian hover:bg-emerald-300 font-bold w-full"
                            data-testid="discover-tp-go"
                        >
                            {tpBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Languages className="h-4 w-4 mr-2" />}
                            {tpBusy ? "Translating…" : tpResult ? "Done" : "Translate & Publish"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Fixture dialog */}
            <Dialog open={!!fxItem} onOpenChange={(o) => !o && setFxItem(null)}>
                <DialogContent className="bg-paper border-border max-w-lg" data-testid="discover-fx-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Download className="h-5 w-5 text-emerald-400" /> Import Fixture
                        </DialogTitle>
                        <DialogDescription className="text-xs text-white/50">
                            Save the match locally · generate a multi-AI prediction · feature on homepage.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm font-bold text-white">{fxItem?.home_team} vs {fxItem?.away_team}</div>
                        <div className="text-[11px] text-white/40">{fxItem?.league} · {fmtKickoff(fxItem?.kickoff)}</div>
                        <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer pt-2">
                            <Checkbox checked={fxPredict} onCheckedChange={setFxPredict} data-testid="discover-fx-predict" />
                            <Sparkles className="h-3.5 w-3.5 text-electric" />
                            Generate multi-AI prediction (Dimbani + Claude + Gemini)
                        </label>
                        <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                            <Checkbox checked={fxFeature} onCheckedChange={setFxFeature} data-testid="discover-fx-feature" />
                            <Star className="h-3.5 w-3.5 text-yellow-300" />
                            Feature on homepage
                        </label>
                        {fxResult && (
                            <Card className="bg-obsidian border-emerald-400/40 p-3 text-sm text-white">
                                <Badge className="bg-emerald-400/20 text-emerald-300 mb-1 text-[10px] uppercase">Imported</Badge>
                                <div>Match id <span className="font-mono text-xs">{fxResult.match_id?.slice(0, 8)}…</span></div>
                                {fxResult.prediction_id && <div className="text-emerald-300 text-xs">Prediction generated ✓</div>}
                                {fxResult.featured && <div className="text-yellow-300 text-xs">Featured on homepage ✓</div>}
                                {fxResult.deduped && <div className="text-white/60 text-xs">(already existed — kept original)</div>}
                            </Card>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            disabled={fxBusy}
                            onClick={submitImportFixture}
                            className="bg-emerald-400 text-obsidian hover:bg-emerald-300 font-bold w-full"
                            data-testid="discover-fx-go"
                        >
                            {fxBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                            {fxBusy ? "Importing…" : fxResult ? "Done" : "Import"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DiscoverWidget;
